import React, { useEffect, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Label } from "@progress/kendo-react-labels";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { NumericTextBox, TextBox } from "@progress/kendo-react-inputs";
import { Checkbox } from "@progress/kendo-react-inputs";
import styles from "./index.module.scss";
import { getActiveInterestSchemes, addInterestScheme, updateInterestScheme } from "services/interestCalculatorService";
import { Loader } from "@progress/kendo-react-indicators";
import LevelInterestGrid from "./LevelInterestGrid";
import editIcon from "assets/common/editBoxIcon.png";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

const InterestCalculation = () => {
  const [interestScheme, setInterestScheme] = useState([]);
  const [noInterestThreshold, setNoInterestThreshold] = useState(1000.00);
  const [interestAfterDays, setInterestAfterDays] = useState(30);
  const [isActive, setIsActive] = useState(true);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [loadingSchemes, setLoadingSchemes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatedSchemeData, setUpdatedSchemeData] = useState(null);
  const navigate = useNavigate();


  useEffect(() => {
    (async () => {
      setLoadingSchemes(true);
      try {
        const schemesData = await getActiveInterestSchemes();
        setInterestScheme(schemesData || []);

        if (schemesData?.length > 0) {
          const firstScheme = schemesData[0];
          setSelectedScheme(firstScheme);
          setNoInterestThreshold(firstScheme.minAmount || 1000);
          setInterestAfterDays(firstScheme.minDays || 30);
          setIsActive(firstScheme.active !== false);
        }
      } catch (error) {
        console.error("Failed to fetch interest schemes", error);
      } finally {
        setLoadingSchemes(false);
      }
    })();
  }, []);

  const transformSchemeDataForAPI = (schemeData, isNew = false) => {
    const baseData = {
      schemeName: schemeData.schemeName,
      minAmount: schemeData.minAmount,
      minDays: schemeData.minDays,
      band1: schemeData.band1,
      band2: schemeData.band2,
      band3: schemeData.band3,
      band4: schemeData.band4,
      band5: schemeData.band5,
      band6: schemeData.band6,
      band7: schemeData.band7,
      band8: schemeData.band8,
      band9: schemeData.band9,
      band10: schemeData.band10,
      active: schemeData.active,
      schemeRates: schemeData.schemeRates?.map(rate => ({
        ...(isNew ? {} : { schemeRateId: rate.schemeRateId }),
        level1Rate: rate.level1Rate,
        level2Rate: rate.level2Rate,
        level3Rate: rate.level3Rate,
        level4Rate: rate.level4Rate,
        level5Rate: rate.level5Rate,
        level6Rate: rate.level6Rate,
        level7Rate: rate.level7Rate,
        level8Rate: rate.level8Rate,
        level9Rate: rate.level9Rate,
        level10Rate: rate.level10Rate,
        active: rate.active,
        effectiveFrom: rate.effectiveFrom
      })) || []
    };

    if (!isNew) {
      baseData.interestSchemeId = schemeData.interestSchemeId;
    }

    return baseData;
  };

  const handleSave = async () => {
    if (!selectedScheme) {
      toast.error("No scheme selected");
      return;
    }
    setSaving(true);
    try {
      const finalSchemeData = {
        ...selectedScheme,
        schemeName: selectedScheme?.schemeName,
        minAmount: noInterestThreshold,
        minDays: interestAfterDays,
        active: isActive,
        ...updatedSchemeData,
      };

      let result;
      if (selectedScheme?.isNew) {
        const { isNew, interestSchemeId, ...createData } = finalSchemeData;
        const apiPayload = transformSchemeDataForAPI(createData, true);
        result = await addInterestScheme(apiPayload);
        toast.success("Scheme created successfully");
      } else {
        const apiPayload = transformSchemeDataForAPI(finalSchemeData);
        result = await updateInterestScheme(finalSchemeData.interestSchemeId, apiPayload);
        toast.success("Scheme updated successfully");
      }

      const updatedSchemes = await getActiveInterestSchemes();
      setInterestScheme(updatedSchemes || []);

      if (selectedScheme?.isNew && result?.data?.data) {
        setSelectedScheme(result.data.data);
        setIsEditingName(false);
      }

    } catch (error) {
      toast.error("Failed to save scheme");
    } finally {
      setSaving(false);
    }
  };

  const handleNewScheme = () => {
    setLoading(true);

    const newSchemeData = {
      interestSchemeId: null,
      schemeName: "New Scheme",
      minAmount: 1000.00,
      minDays: 30,
      band1: 0.00,
      band2: 0.00,
      band3: 0.00,
      band4: 0.00,
      band5: 0.00,
      band6: 0.00,
      band7: 0.00,
      band8: 0.00,
      band9: 0.00,
      band10: 0.00,
      active: true,
      schemeRates: [{
        schemeRateId: null,
        effectiveFrom: new Date().toISOString().split('T')[0],
        level1Rate: 0.00,
        level2Rate: 0.00,
        level3Rate: 0.00,
        level4Rate: 0.00,
        level5Rate: 0.00,
        level6Rate: 0.00,
        level7Rate: 0.00,
        level8Rate: 0.00,
        level9Rate: 0.00,
        level10Rate: 0.00,
        active: true
      }],
      isNew: true
    };

    const updatedSchemes = [newSchemeData, ...interestScheme];
    setInterestScheme(updatedSchemes);

    setSelectedScheme(newSchemeData);

    setIsEditingName(true);

    setNoInterestThreshold(newSchemeData.minAmount);
    setInterestAfterDays(newSchemeData.minDays);
    setIsActive(newSchemeData.active);
    setLoading(false);
  };

  const handleSchemeChange = async (e) => {
    const value = e.value;
    setSelectedScheme(value);
    setIsEditingName(false);

    if (value) {
      if (value.isNew) {
        setNoInterestThreshold(value.minAmount || 1000);
        setInterestAfterDays(value.minDays || 30);
        setIsActive(value.active !== false);
      } else {
        const selectedSchemeData = interestScheme.find(scheme => scheme.interestSchemeId === value.interestSchemeId);
        if (selectedSchemeData) {
          setNoInterestThreshold(selectedSchemeData.minAmount || 1000);
          setInterestAfterDays(selectedSchemeData.minDays || 30);
          setIsActive(selectedSchemeData.active !== false);
        }
      }
    }
  };

  const handleSchemeNameChange = (e) => {
    const newName = e.value;

    if (selectedScheme) {
      const updatedScheme = { ...selectedScheme, schemeName: newName };
      setSelectedScheme(updatedScheme);

      const updatedSchemes = interestScheme.map(scheme =>
        scheme.interestSchemeId === selectedScheme.interestSchemeId
          ? updatedScheme
          : scheme
      );
      setInterestScheme(updatedSchemes);
    }
  };

  const handleEditNameToggle = () => {
    setIsEditingName(!isEditingName);
  };

  const handleThresholdChange = (e) => {
    const newValue = e.value;
    setNoInterestThreshold(newValue);
  };

  const handleDaysChange = (e) => {
    const newValue = e.value;
    setInterestAfterDays(newValue);
  };

  const handleActiveChange = (e) => {
    const newValue = e.value;
    setIsActive(newValue);
  };

  const handleSchemeDataUpdate = (updatedData) => {
    setUpdatedSchemeData(updatedData);
  };

  const handleBandUpdate = (bands) => {
    const bandData = {};
    bands.forEach(band => {
      bandData[`band${band.level}`] = band.threshold;
    });

    setUpdatedSchemeData(prev => ({
      ...prev,
      ...bandData
    }));
  };

  const handleRatesUpdate = (schemeRates) => {
    setUpdatedSchemeData(prev => ({
      ...prev,
      schemeRates: schemeRates
    }));
  };

  return (
    <div className="d-flex flex-column h-100 w-100">
      <div className="flex-shrink-0">
        <div className="d-flex gap-4 align-items-end">
          <div className="d-flex flex-column gap-1 justify-content-end">
            <Label>Interest Scheme</Label>
            <div className="d-flex gap-2 align-items-center">
              {isEditingName ? (
                <TextBox
                  style={{ width: "250px", height: "35px" }}
                  value={selectedScheme?.schemeName || ""}
                  onChange={handleSchemeNameChange}
                  placeholder="Enter scheme name"
                />
              ) : (
                <ComboBox
                  iconClassName="wa"
                  style={{ width: "250px", height: "35px" }}
                  data={interestScheme}
                  textField="schemeName"
                  dataItemKey="interestSchemeId"
                  value={selectedScheme}
                  onChange={handleSchemeChange}
                  loading={loadingSchemes}
                  placeholder="Select scheme"
                />
              )}
              {selectedScheme && (
                <Button
                  className="bg-white border-1 p-1 outline-none focus:outline-none focus:ring-0 shadow-none"
                  style={{ width: "25px", height: "25px" }}
                  onClick={handleEditNameToggle}
                  title={isEditingName ? "Cancel Edit" : "Edit scheme name"}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                    {isEditingName ? (
                      <svg width="11" height="11" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1L7 7" stroke="#FF0D0D" stroke-linecap="round" />
                        <path d="M7 1L1 7" stroke="#FF0D0D" stroke-linecap="round" />
                      </svg>
                    ) : (
                      <img src={editIcon} alt="Edit" style={{ width: "13px", height: "13px" }} />
                    )}
                  </div>
                </Button>
              )}
            </div>
          </div>
          <Button
            className="common-btn"
            style={{ height: "35px" }}
            onClick={handleNewScheme}
            disabled={loading}
          >
            {loading ? (
              <Loader
                size="small"
                type="infinite-spinner"
                className="text-white"
              />
            ) : (
              "New Scheme"
            )}
          </Button>
        </div>
        <div className={`d-flex flex-row align-items-end gap-2 gap-xl-4 my-4 pt-4 ${styles.thresholdDaysRow}`}>
          <div className="d-flex flex-column gap-1 justify-content-end">
            <Label>No Interest Below Threshold</Label>
            <div className="right-align">
              <NumericTextBox
                value={noInterestThreshold}
                onChange={handleThresholdChange}
                format="n2"
                style={{
                  width: "250px",
                  height: "35px"
                }}
                spinners={false}
                placeholder="Enter Threshold"
                min={0}
              />
            </div>
          </div>
          <div className="d-flex flex-column gap-1 justify-content-end" style={{ flex: "1 1 180px", maxWidth: "250px" }}>
            <Label>Interest After (n) Days</Label>
            <div className="d-flex gap-1">
              <div className="right-align" style={{ width: "100%" }}>
                <NumericTextBox
                  value={interestAfterDays}
                  onChange={handleDaysChange}
                  style={{
                    width: "100%",
                    height: "35px"
                  }}
                  spinners={false}
                  placeholder="Enter Days"
                  min={0}
                />
              </div>
            </div>
          </div>
          <div className="mb-2">
            <Checkbox
              checked={isActive}
              onChange={handleActiveChange}
              label="Active"
            />
          </div>
        </div>
      </div>

      <div className="flex-grow-1 d-flex flex-column overflow-hidden mb-2 p-1" style={{ minHeight: 0 }}>
        <LevelInterestGrid
          onBandUpdate={handleBandUpdate}
          onRatesUpdate={handleRatesUpdate}
          onSchemeDataUpdate={handleSchemeDataUpdate}
          selectedScheme={selectedScheme}
          loading={loadingSchemes}
        />
      </div>
      <div className="d-flex justify-content-end gap-2 align-items-center px-4 py-3 bg-white flex-shrink-0" style={{ borderTop: "1px solid #e8e8e8", margin: "0rem -2.7rem" }}>
        <Button
          className="common-btn bgBtnSecondary"
          onClick={() => { navigate(-1) }}
        >
          Cancel
        </Button>
        <Button
          className="common-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <Loader
              size="small"
              type="pulsing"
              className="text-white"
            />
          ) : (
            "Save"
          )}
        </Button>
      </div>
    </div>
  );
};

export default InterestCalculation; 